import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Reemplaza el tooltip nativo del navegador (atributo `title`) por uno con el
// estilo del proyecto, en TODA la app, sin tocar los ~534 `title` repartidos
// en ~200 componentes (medido el 5-oct-2026).
//
// Cómo: al entrar el puntero en un elemento con `title`, el atributo se mueve
// a `data-native-title` (así el navegador no muestra el suyo) y se pinta una
// burbuja propia; al salir, `title` vuelve a su lugar. El elemento conserva su
// `title` todo el tiempo salvo mientras el puntero está encima, así que los
// lectores de pantalla y los tests que lo consultan no cambian.
//
// Mismo aspecto que components/ui/Tooltip (fondo gray-900, texto 10 px). El
// texto se pinta como texto, no como HTML: viene de `title`, que puede traer
// datos de usuario (placas, nombres).
const SHOW_DELAY_MS = 300;

type Tip = { text: string; x: number; y: number; below: boolean };

export default function TitleTooltips() {
  const [tip, setTip] = useState<Tip | null>(null);
  const current = useRef<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastX = useRef(0);

  useEffect(() => {
    const restore = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      const el = current.current;
      if (el && el.hasAttribute("data-native-title")) {
        el.setAttribute("title", el.getAttribute("data-native-title") || "");
        el.removeAttribute("data-native-title");
      }
      current.current = null;
      setTip(null);
    };

    const onOver = (e: PointerEvent) => {
      const target = e.target as Element | null;
      // Ya hay burbuja nativa (su `title` está escondido) y el puntero entra a
      // un <Tooltip> propio dentro del mismo elemento: se cede el lugar.
      const innerTrigger = target?.closest?.("[data-tooltip-trigger]");
      if (innerTrigger && current.current?.contains(innerTrigger)) {
        restore();
        return;
      }
      const el = target?.closest?.("[title]") as HTMLElement | null;
      if (!el || el === current.current) return;
      // Elementos SVG (íconos con title propio) quedan como están.
      if (el instanceof SVGElement) return;
      // Si entre el puntero y el elemento con `title` hay un <Tooltip> del
      // proyecto (data-tooltip-trigger), gana ése. Sin esto, en la fila de
      // unidad (title = nombre · placas) salían DOS burbujas encimadas al
      // pasar sobre un semáforo, que tiene su propio Tooltip.
      const trigger = target?.closest?.("[data-tooltip-trigger]");
      if (trigger && el.contains(trigger)) {
        restore();
        return;
      }
      const text = el.getAttribute("title")?.trim();
      restore();
      if (!text) return;
      current.current = el;
      el.setAttribute("data-native-title", el.getAttribute("title") || "");
      el.removeAttribute("title");
      lastX.current = e.clientX;
      timer.current = setTimeout(() => {
        if (current.current !== el || !el.isConnected) return;
        const r = el.getBoundingClientRect();
        const below = r.top < 40;
        // En elementos anchos (la fila de unidad mide ~500 px) la burbuja
        // centrada caía encima de los semáforos de la fila de arriba. Sale
        // donde está el puntero, acotada al ancho del elemento.
        const x = Math.min(Math.max(lastX.current, r.left + 12), r.right - 12);
        setTip({ text, x, y: below ? r.bottom + 6 : r.top - 6, below });
      }, SHOW_DELAY_MS);
    };

    const onOut = (e: PointerEvent) => {
      const el = current.current;
      if (!el) return;
      const to = e.relatedTarget as Node | null;
      if (to && el.contains(to)) return;
      restore();
    };

    const onMove = (e: PointerEvent) => {
      if (current.current) lastX.current = e.clientX;
    };
    document.addEventListener("pointermove", onMove, { capture: true, passive: true });
    document.addEventListener("pointerover", onOver, true);
    document.addEventListener("pointerout", onOut, true);
    document.addEventListener("pointerdown", restore, true);
    window.addEventListener("scroll", restore, true);
    return () => {
      restore();
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerover", onOver, true);
      document.removeEventListener("pointerout", onOut, true);
      document.removeEventListener("pointerdown", restore, true);
      window.removeEventListener("scroll", restore, true);
    };
  }, []);

  if (!tip) return null;
  return createPortal(
    <div
      role="tooltip"
      className="fixed z-[9999] pointer-events-none"
      style={{ left: tip.x, top: tip.y, transform: tip.below ? "translate(-50%, 0)" : "translate(-50%, -100%)" }}
    >
      <div className="max-w-xs px-2 py-1 bg-gray-900 text-white text-[10px] font-medium rounded shadow-lg text-center animate-fadeIn">
        {tip.text}
      </div>
    </div>,
    document.body,
  );
}
